﻿import { AssetInformation } from './AssetInformation';
import {
  ValidateQuickQuoteResponse,
  QuickQuoteToProposalConversionResultModel
} from './ResponseModels';
import RequestService from '@/common/requestService';
import { ApiResponse } from '@/interfaces/RequestService';
import Utility from '@/common/UtilityFunctions.js';

export type QuickQuoteStatus = {
  Type: string;
  Name: string;
  StateEnum: number;
  PolicyCacheId: number;
  ReadyToConvert: boolean;
  Asset: AssetInformation | null;
};

export async function validateProposal(quickQuote: QuickQuoteStatus): Promise<void> {
  quickQuote.StateEnum = 1;

  const url = Utility.constants().API.quickquote_validate_single;
  const params = { policyCacheId: quickQuote.PolicyCacheId };
  return RequestService.get<ValidateQuickQuoteResponse>(url, {}, 'json', params).then(
    (response: ApiResponse<ValidateQuickQuoteResponse>) => {
      quickQuote.StateEnum = 0;
      quickQuote.ReadyToConvert = response.data.Valid;
      if (response.data.Valid === false) {
        if (quickQuote.Asset === undefined || quickQuote.Asset === null)
          quickQuote.Asset = {
            ContactCacheId: response.data.ContactCacheId,
            Firstname: response.data.Name
          } as AssetInformation;
      }
    }
  );
}

export function readyForConversion(quickQuotes: QuickQuoteStatus[]): boolean {
  if (quickQuotes === undefined) {
    return false;
  }

  const isNotValid = (element: QuickQuoteStatus) => element.ReadyToConvert !== true;
  const hit = quickQuotes.findIndex(isNotValid);
  return hit === -1;
}

export async function canConvert(quickQuotes: QuickQuoteStatus[]): Promise<boolean> {
  await Promise.all(quickQuotes.map(async (quickQuote) => validateProposal(quickQuote)));
  return readyForConversion(quickQuotes);
}

function generateModel(quickQuote: QuickQuoteStatus) {
  if (quickQuote.Asset === null) {
    return;
  }
  return {
    PolicyCacheId: quickQuote.PolicyCacheId,
    ContactCacheId: quickQuote.Asset.ContactCacheId,
    Firstname: quickQuote.Asset.Firstname,
    Lastname: quickQuote.Asset.Lastname,
    Cpr: quickQuote.Asset.Cpr
  };
}

export async function updateProposal(quickQuote: QuickQuoteStatus): Promise<void> {
  const model = generateModel(quickQuote);
  const headers = {
    'Content-Type': 'application/json'
  };

  await RequestService.post(
    Utility.constants().API.quickquote_update_single_contact_cpr,
    model,
    headers
  );
}

// This call can take up to a few minutes based on the number of quick quotes to convert.
// So run another "thread" by using setTimeout that checks the progress of this call
export async function initiateConversion(quickQuote: QuickQuoteStatus): Promise<void> {
  const params = { policyCacheId: quickQuote.PolicyCacheId };
  const url = Utility.constants().API.quickquote_convert_single;
  const response = await RequestService.post<QuickQuoteToProposalConversionResultModel>(
    url,
    {},
    {},
    'json',
    params
  );
  quickQuote.StateEnum = response.data.StateEnum;
  if (quickQuote.StateEnum === 3) {
    throw new Error('converstion-error' + quickQuote.PolicyCacheId);
  }
}

export async function deleteQuickQuote(quickQuoteId: number): Promise<void> {
  const params = { quickQuoteId };
  await RequestService.delete(Utility.constants().API.quickquote_delete, {}, 'json', params);
}
